let channel: MessageChannel | undefined;
let nextId = 0;
const pending = new Map<number, { callback: () => void; unpaced: boolean }>();
let posted = false;
let postedViaMicrotask = false;
let flushing = false;
let workerBudgetMs = 4;
let workerTaskLimit = 1000;

/** Explicit worker-only research control; browser and Node policies stay fixed. */
export function configureWorkerTaskBudget(ms = 4, tasks = 1000) {
  if (typeof window !== 'undefined' || !('importScripts' in globalThis))
    throw Error('Worker task budget requires a worker host');
  if (
    !Number.isFinite(ms) ||
    ms < 0.25 ||
    ms > 4 ||
    !Number.isSafeInteger(tasks) ||
    tasks < 1 ||
    tasks > 1000
  )
    throw Error('Invalid bounded worker task budget');
  workerBudgetMs = ms;
  workerTaskLimit = tasks;
}

// Keep positive wakeups separate from zero-delay propagation. A queued input
// microtask must not consume one before its actual MessagePort task runs.
let timerChannel: MessageChannel | undefined;
let nextTimerId = 0;
const readyTimers = new Map<number, () => void>();

function postWork(startImmediately = false) {
  if (posted || flushing || pending.size === 0) return;
  posted = true;
  postedViaMicrotask = startImmediately;
  // Start an input's propagation before the browser handles another input or
  // an expensive paint. Continuations must still yield for feedback circuits.
  if (startImmediately) queueMicrotask(flushWork);
  else channel!.port2.postMessage(null);
}

function flushWork() {
  posted = false;
  postedViaMicrotask = false;
  flushing = true;
  // Batch cheap propagation waves, but yield to input/paint even for an
  // oscillator. Posting every individual gate wave can itself take longer
  // than an authored control pulse in a large circuit.
  const scheduling = (
    (typeof window !== 'undefined'
      ? window.navigator
      : globalThis.navigator) as Navigator & {
      scheduling?: {
        isInputPending?: (options: { includeContinuous: boolean }) => boolean;
      };
    }
  )?.scheduling;
  const canCheckInput = typeof scheduling?.isInputPending === 'function';
  const started = performance.now();
  let tasks = 0;
  try {
    while (pending.size && tasks < workerTaskLimit) {
      const [id, task] = pending.entries().next().value!;
      // Extend only Maximum-speed work when input can interrupt the batch.
      // Unsupported browsers retain the original 4 ms bound. The 16 ms bound
      // remains unconditional, so painting and unrelated work still get turns.
      const deadline = started + (task.unpaced && canCheckInput ? 16 : workerBudgetMs);
      if (
        tasks &&
        (performance.now() >= deadline ||
          (task.unpaced &&
            canCheckInput &&
            scheduling!.isInputPending!({ includeContinuous: true })))
      )
        break;
      pending.delete(id);
      tasks++;
      task.callback();
      if (performance.now() >= deadline) break;
    }
  } finally {
    flushing = false;
    postWork();
  }
}

export interface SimulationTaskCheckpoint {
  remaining: number;
  order: number;
  kind: 'work' | 'timer' | 'fallback';
  stage: 'waiting' | 'ready' | 'microtask';
}
interface TrackedTaskState extends Omit<SimulationTaskCheckpoint, 'remaining'> {
  due: number;
}
const taskStates = new WeakMap<() => void, TrackedTaskState>();
let taskOrder = 0;
let callbackDepth = 0;
function messageChannelHost() {
  return typeof window !== 'undefined'
    ? window.MessageChannel
    : 'importScripts' in globalThis
      ? globalThis.MessageChannel
      : undefined;
}

/** Native delivery policy. Restore retains the host queue/stage of pending work. */
function scheduleTask(
  callback: () => void,
  delay: number,
  unpaced: boolean,
  state: TrackedTaskState,
  restored?: SimulationTaskCheckpoint,
): () => void {
  const MessageChannelHost = messageChannelHost();
  if (!MessageChannelHost) {
    const timer = setTimeout(callback, delay);
    return () => clearTimeout(timer);
  }
  if (delay > 0 || restored?.kind === 'timer') {
    let active = true;
    let queuedId: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ready = () => {
      timer = undefined;
      if (!active) return;
      state.stage = 'ready';
      // Ordering is the actual port publication order, not timer registration.
      state.order = ++taskOrder;
      if (!timerChannel) {
        timerChannel = new MessageChannelHost();
        timerChannel.port1.onmessage = ({ data }: MessageEvent<number>) => {
          const run = readyTimers.get(data);
          readyTimers.delete(data);
          run?.();
        };
      }
      queuedId = nextTimerId++;
      readyTimers.set(queuedId, () => {
        active = false;
        queuedId = undefined;
        callback();
      });
      timerChannel.port2.postMessage(queuedId);
    };
    if (restored?.stage === 'ready') ready();
    else timer = setTimeout(ready, delay);
    return () => {
      if (!active) return;
      active = false;
      if (timer !== undefined) clearTimeout(timer);
      if (queuedId !== undefined) readyTimers.delete(queuedId);
      timer = undefined;
      queuedId = undefined;
    };
  }
  if (!channel) {
    channel = new MessageChannelHost();
    channel.port1.onmessage = flushWork;
  }
  const id = nextId++;
  pending.set(id, { callback, unpaced });
  // Passive native/input-handler captures may precede the initial microtask;
  // worker RPC captures can also retain work already queued for a port turn.
  postWork(!restored || restored.stage === 'microtask');
  return () => {
    pending.delete(id);
  };
}

/** Metadata only: live native task delivery and queue policy are unchanged. */
export function captureSimulationTask(
  cancel: (() => void) | null | undefined,
): SimulationTaskCheckpoint | null {
  const state = cancel && taskStates.get(cancel);
  return state
    ? {
        remaining:
          state.stage === 'ready' ? 0 : Math.max(0, state.due - performance.now()),
        order: state.order,
        kind: state.kind,
        stage:
          state.kind === 'work'
            ? postedViaMicrotask
              ? 'microtask'
              : 'ready'
            : state.stage,
      }
    : null;
}
function trackedTask(
  callback: () => void,
  delay: number,
  unpaced: boolean,
  restored?: SimulationTaskCheckpoint,
) {
  const host = messageChannelHost();
  if (restored) assertSimulationTaskHost(restored);
  const remaining = Math.max(0, delay) || (!host && callbackDepth > 0 ? 1 : 0);
  const state: TrackedTaskState = {
    due: performance.now() + remaining,
    order: ++taskOrder,
    kind: restored?.kind ?? (!host ? 'fallback' : delay > 0 ? 'timer' : 'work'),
    stage:
      restored?.stage === 'microtask'
        ? 'ready'
        : (restored?.stage ?? (host && delay <= 0 ? 'ready' : 'waiting')),
  };
  let cancel: () => void;
  const inner = scheduleTask(
    () => {
      taskStates.delete(cancel);
      callbackDepth++;
      try {
        callback();
      } finally {
        callbackDepth--;
      }
    },
    delay,
    unpaced,
    state,
    restored,
  );
  cancel = () => {
    taskStates.delete(cancel);
    inner();
  };
  taskStates.set(cancel, state);
  return cancel;
}
export function scheduleSimulationTask(
  callback: () => void,
  delay: number,
  unpaced = false,
): () => void {
  return trackedTask(callback, delay, unpaced);
}
export function assertSimulationTaskHost(checkpoint: SimulationTaskCheckpoint) {
  if ((checkpoint.kind === 'fallback') === Boolean(messageChannelHost()))
    throw Error('Checkpoint task host mismatch');
}
export function restoreSimulationTask(
  callback: () => void,
  checkpoint: SimulationTaskCheckpoint,
  unpaced = false,
): () => void {
  return trackedTask(callback, checkpoint.remaining, unpaced, checkpoint);
}
